import type {
  StageView,
  ViewKind,
  ViewLevel,
  ViewSource,
  AssistantCard,
  InboxItem,
} from '@secretario/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import { registerLayer } from '../../lib/layers.js';
import {
  INITIAL_STAGE,
  presenceOf,
  stageReducer,
  type StageMode,
  type StageState,
} from '../../lib/stage/stageReducer.js';

const MODE_KEY = 'secretario.palco.modo';
/** Cartão de aviso some sozinho. */
const NOTICE_MS = 4200;

type ShowInput = {
  kind: ViewKind;
  level?: ViewLevel;
  source?: ViewSource;
  text?: string;
  /** O pedido que abriu a visão, nas palavras da pessoa. */
  asked?: string;
  /** Nome do item citado, para a linha dele pulsar. */
  focus?: string;
  /** Vem da fila do ao vivo, que já deu o ritmo da tela. */
  force?: boolean;
};

/** Quanto tempo um cartão com dado e ação fica na tela antes de sair sozinho. */
const CARD_MS = 9000;
/** A pergunta espera mais: a pessoa pode estar pensando na resposta. */
const ASK_MS = 30_000;

export type StageApi = {
  state: StageState;
  presence: 'full' | 'bust';
  show: (input: ShowInput) => void;
  /** Aviso curto em cartão (ex.: "Modo silencioso ligado"). */
  notice: (text: string) => void;
  /** Cartão com dado e ação: o que o Secretário acabou de fazer ou conferir. */
  card: (card: AssistantCard) => void;
  /** Pergunta de esclarecimento com opções (fica até a resposta). */
  ask: (question: string, options: string[]) => void;
  /** Mensagem de outro app (WhatsApp, SMS): o cartão com Responder; fica até a pessoa decidir. */
  inbox: (item: InboxItem, options?: InboxOptions) => void;
  close: (target?: 'top' | 'all' | string) => void;
  reshow: () => void;
  /** O pedido entendido, quando chega depois de o painel abrir. */
  asked: (text: string) => void;
  setMode: (mode: StageMode) => void;
};

export type InboxOptions = {
  /** Rascunho que já veio pronto (pela voz): abre direto na revisão. */
  draft?: string | undefined;
  /** Todas as falas da conversa que esperam resposta. */
  lines?: { text: string; at: number }[] | undefined;
  /** Quantas outras conversas esperam atrás desta. */
  more?: number | undefined;
  onNext?: (() => void) | undefined;
};

const StageContext = createContext<StageApi | null>(null);

function readMode(): StageMode {
  try {
    return window.localStorage.getItem(MODE_KEY) === 'silent' ? 'silent' : 'voice';
  } catch {
    return 'voice';
  }
}

/** A pessoa está digitando em algum campo agora? Nada abre por cima dela. */
function isTyping(): boolean {
  const active = document.activeElement;
  return (
    active instanceof HTMLInputElement ||
    active instanceof HTMLTextAreaElement ||
    (active instanceof HTMLElement && active.isContentEditable)
  );
}

export function StageProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(stageReducer, INITIAL_STAGE, (initial) => ({
    ...initial,
    mode: readMode(),
  }));

  const show = useCallback((input: ShowInput) => {
    const level = input.level ?? 'panel';
    const source = input.source ?? 'rail';
    const view: StageView = {
      id: input.kind === 'notice' ? `notice:${Date.now()}` : `${source}:${input.kind}`,
      kind: input.kind,
      level,
      source,
      ...(input.text ? { text: input.text } : {}),
      ...(input.asked ? { asked: input.asked } : {}),
      ...(input.focus ? { focus: input.focus } : {}),
    };
    dispatch({
      type: 'show',
      view,
      now: Date.now(),
      typing: isTyping(),
      ...(input.force ? { force: true } : {}),
    });
  }, []);
  const notice = useCallback(
    (text: string) => show({ kind: 'notice', level: 'card', text }),
    [show],
  );
  const card = useCallback((content: AssistantCard) => {
    const key = content.undo?.id ?? `${content.kind}:${content.art ?? ''}:${content.title}`;
    dispatch({
      type: 'show',
      view: {
        id: `card:${key}`,
        kind: 'notice',
        level: 'card',
        source: 'assistant',
        card: content,
      },
      now: Date.now(),
      typing: isTyping(),
    });
  }, []);
  const ask = useCallback((question: string, options: string[]) => {
    dispatch({
      type: 'show',
      view: {
        id: 'ask',
        kind: 'notice',
        level: 'card',
        source: 'assistant',
        ask: { question, options },
      },
      now: Date.now(),
      typing: isTyping(),
    });
  }, []);
  const inbox = useCallback(
    (item: InboxItem, { draft, lines, more, onNext }: InboxOptions = {}) => {
      dispatch({
        type: 'show',
        view: {
          id: `inbox:${item.key}`,
          kind: 'notice',
          level: 'card',
          source: 'assistant',
          inbox: {
            item,
            ...(draft ? { draft } : {}),
            ...(lines && lines.length > 0 ? { lines } : {}),
            ...(more ? { more } : {}),
            ...(onNext ? { onNext } : {}),
          },
        },
        now: Date.now(),
        typing: isTyping(),
        force: true,
      });
    },
    [],
  );
  const close = useCallback(
    (target: 'top' | 'all' | string = 'top') => dispatch({ type: 'close', target }),
    [],
  );
  const reshow = useCallback(() => dispatch({ type: 'reshow' }), []);
  const asked = useCallback((text: string) => dispatch({ type: 'asked', asked: text }), []);
  const setMode = useCallback((mode: StageMode) => {
    try {
      window.localStorage.setItem(MODE_KEY, mode);
    } catch {
      /* sem armazenamento: vale só nesta abertura */
    }
    dispatch({ type: 'mode', mode });
  }, []);

  // O voltar do aparelho fecha o que estiver aberto no palco, de cima para baixo.
  const panelId = state.panel?.id;
  useEffect(() => {
    if (!panelId) return;
    return registerLayer({ id: panelId, kind: 'panel', close: () => close(panelId) });
  }, [panelId, close]);
  const topCardId = state.cards[state.cards.length - 1]?.id;
  useEffect(() => {
    if (!topCardId) return;
    return registerLayer({ id: topCardId, kind: 'card', close: () => close(topCardId) });
  }, [topCardId, close]);

  // Teclado: Esc fecha o que estiver por cima.
  const anyOpen = state.panel !== null || state.cards.length > 0;
  useEffect(() => {
    if (!anyOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close('top');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [anyOpen, close]);

  // Avisos somem sozinhos.
  useEffect(() => {
    // Aviso de texto sai rápido; cartão com dado e ação, e painel que virou
    // cartão porque a pessoa digitava, ficam um pouco mais. Nenhum fica para sempre.
    // A mensagem de outra pessoa espera a decisão: nunca sai sozinha.
    const timed = state.cards.filter((item) => !item.inbox);
    if (timed.length === 0) return;
    const timers = timed.map((item) =>
      setTimeout(
        () => close(item.id),
        item.ask ? ASK_MS : item.kind === 'notice' && !item.card ? NOTICE_MS : CARD_MS,
      ),
    );
    return () => timers.forEach(clearTimeout);
  }, [state.cards, close]);

  const api = useMemo<StageApi>(
    () => ({
      state,
      presence: presenceOf(state),
      show,
      notice,
      card,
      ask,
      close,
      reshow,
      inbox,
      asked,
      setMode,
    }),
    [state, show, notice, card, ask, inbox, close, reshow, asked, setMode],
  );
  // Só em desenvolvimento: as provas de tela abrem painéis e cartões por aqui.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as unknown as { __stage?: StageApi }).__stage = api;
  }, [api]);
  return <StageContext.Provider value={api}>{children}</StageContext.Provider>;
}

export function useStage(): StageApi {
  const api = useContext(StageContext);
  if (!api) throw new Error('useStage fora do StageProvider');
  return api;
}

/** Para telas que funcionam com e sem o palco (ex.: durante a liberação por pessoa). */
export function useOptionalStage(): StageApi | null {
  return useContext(StageContext);
}
